import swal from 'sweetalert2';
import moment from 'moment-timezone';
import get from 'lodash/get';
import vueMixins from 'viralstyle-ui/src/vueMixins';
import hateoas from 'viralstyle-ui/src/hateoas';
import storage from 'viralstyle-ui/src/storage';

import config from 'config';
import settingsApi from 'domains/settings/_settings-api';

export const getPayoutTier = async ({ rootState, state, dispatch, commit }) => {
  if (!Object.keys(state.tierData).length) {
    let response = await dispatch('get', rootState.currentUser._links.get_client_tier.href);

    commit('updateState', { object: state, property: 'tierData', value: response.data });

    return response;
  }
};

export const downloadPayoutCsv = async ({ dispatch }, payout) => {
  let response = await dispatch('get', payout._links.breakdown_csv.href);

  dispatch('downloadFile', { response, fileName: `${moment(payout.created_at).format('MM_DD_YYYY')}-payout-breakdown.csv` });

  return response;
};

export const downloadFullPayoutCsv = async ({ dispatch }, { url, type }) => {
  let response = await dispatch('get', url);

  dispatch('downloadFile', { response, fileName: `${moment().format('MM_DD_YYYY')}-payout-info_${type}.csv` });

  return response;
};

export const getAvailablePayouts = async ({ commit, dispatch, state }, lazy) => {
  if (!state.availablePayouts.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getAvailablePayouts(state.availablePayouts));

    commit('setPayouts', { type: 'availablePayouts', payouts: response.data });

    return response;
  }
};

export const getRequestedPayouts = async ({ commit, dispatch, state }, lazy) => {
  if (!state.requestedPayouts.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getRequestedPayouts(state.requestedPayouts));

    commit('setPayouts', { type: 'requestedPayouts', payouts: response.data });

    return response;
  }
};

export const getPendingPayouts = async ({ commit, dispatch, state }, lazy) => {
  if (!state.pendingPayouts.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getPendingPayouts(state.pendingPayouts));

    commit('setPayouts', { type: 'pendingPayouts', payouts: response.data });

    return response;
  }
};

export const getPayoutDeductions = async ({ commit, dispatch, state }, lazy) => {
  if (!state.payoutDeductions.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getPayoutDeductions(state.payoutDeductions));

    commit('setPaginatableCollection', { paginatableCollection: state.payoutDeductions, data: response.data });

    return response;
  }
};

export const getPayoutDetails = async ({ state, dispatch, commit }, { payout, individual }) => {
  commit('updateState', { object: state, property: 'currentPayout', value: payout });

  commit('lockScroll', true);

  let response = await dispatch('get', payout._links.self.href);


  if (individual) {
    let value = {
      amount: response.data.amount,
      payouts: [response.data],
    };

    commit('updateState', { object: state, property: 'currentPayoutDetails', value });
  } else {
    commit('updateState', { object: state, property: 'currentPayoutDetails', value: response.data });
  }

  return response;
};

export const validateAndSavePayout = async ({ state, dispatch, commit }, { saveMethod, method, validator, paymentMethodInfo, paymentInfo = {}, routeName, skipSavePayment }) => {
  try {
    let response,
      valid = await validator.validateAll(paymentMethodInfo);

    if (valid) {
      commit('updateState', { object: state.paymentInfoSubmitted, property: method, value: false });

      if (!skipSavePayment) {
        response = await dispatch('savePaymentMethod', saveMethod);
      }

      if (routeName.indexOf('settingsPayoutsReferrals') !== -1) {
        dispatch('requestReferralPayout', { method, paymentInfo });
      } else if (routeName.indexOf('settingsPayoutsAffiliate') !== -1) {
        dispatch('requestAffiliatePayout', { payment_method: method, payment_info: paymentInfo });
      } else if (routeName.indexOf('settingsPayoutsCampaigns') !== -1) {
        dispatch('requestCampaignPayout', { payment_method: method, payment_info: paymentInfo });
      } else {
        commit('addNotification', 'Payout method not yet supported.');
      }

      commit('togglePaymentModal', { modal: method.toLowerCase(), value: false });
      commit('lockScroll', false);

      return response;
    } else {
      commit('updateState', { object: state.paymentInfoSubmitted, property: method, value: true });
    }
  } catch (error) {
    throw error;
  }
};

export const requestCampaignPayout = async ({ dispatch, commit, state }, { payment_method, payment_info }) => {
  let response,
    payload = {
      payment_method,
      payment_info,
    },
    apiUrl,
    campaignId = get(state.currentPayout, 'campaign_id');

  if (campaignId) {
    apiUrl = hateoas.url('request_campaign_payout', { clientId: storage.get('client-hash'), campaignId });
  } else {
    apiUrl = hateoas.url('request_payout', { clientId: storage.get('client-hash') })
  }

  try {
    response = await dispatch('post', { apiUrl, payload });

    commit('addNotification', 'Campaign Payout Requested!');

    if (campaignId) {
      commit('updateState', { object: state.currentPayout, property: 'payment_status', value: 'IN-PROGRESS' });
    } else {
      commit('resetPayouts', 'availablePayouts');

      dispatch('getAvailablePayouts');
    }
  } catch (error) {
    commit('addNotification', 'Error requesting campaign payout. Please try again later.');

    commit('resetPayouts', 'availablePayouts');

    commit('updateState', { object: state, property: 'currentPayout', value: null });

    dispatch('getAvailablePayouts');

    throw error;
  }

  commit('resetPayouts', 'requestedPayouts');

  commit('resetPaginatableCollection', state.payoutDeductions);

  dispatch('getRequestedPayouts');

  dispatch('getPayoutTotals');

  dispatch('getPaymentInfo');

  commit('updateState', { object: state, property: 'currentPayout', value: null });

  return response;
};

export const requestReferralPayout = async ({ dispatch, commit, state }, { method, paymentInfo }) => {
  let response;

  try {
    response = await dispatch('async', () => settingsApi.requestReferralPayout(method, paymentInfo));

    commit('addNotification', 'Referral payout requested!');

    commit('resetReferrals', 'availableReferrals');

    commit('resetReferrals', 'requestedReferrals');

    dispatch('getAvailableReferrals');

    dispatch('getRequestedReferrals');
  } catch (error) {
    commit('addNotification', 'Error requesting referral payout. Please try again later.');

    commit('resetReferrals', 'availableReferrals');

    commit('resetReferrals', 'requestedReferrals');

    dispatch('getAvailableReferrals');

    dispatch('getRequestedReferrals');

    throw error;
  }

  commit('resetReferrals', 'paidReferrals');

  commit('resetPaginatableCollection', state.payoutDeductions);

  dispatch('getPaidReferrals');

  dispatch('getReferralTotals');

  dispatch('getPayoutTotals');

  dispatch('getPaymentInfo');

  return response;
};

export const requestAffiliatePayout = async ({ state, dispatch, commit }, { payment_method, payment_info }) => {
  let response,
    payload = {
      payment_method,
      payment_info,
    },
    apiUrl,
    payoutId = get(state.currentAffiliatePayout, 'id');

  if (payoutId) {
    apiUrl = hateoas.url('request_individual_affiliate_payout', { clientId: storage.get('client-hash'), payoutId });
  } else {
    apiUrl = hateoas.url('request_affiliate_payout', { clientId: storage.get('client-hash') })
  }

  try {
    response = await dispatch('post', { apiUrl, payload });

    commit('addNotification', 'Affiliate Payout Requested!');

    if (payoutId) {
      commit('updateState', { object: state.currentAffiliatePayout, property: 'payment_status', value: 'IN-PROGRESS' });
    } else {
      commit('resetPayouts', 'availableAffiliatePayouts');

      dispatch('getAvailableAffiliatePayouts');
    }
  } catch (error) {
    commit('addNotification', 'Error requesting affiliate payout. Please try again later.');

    commit('resetPayouts', 'availableAffiliatePayouts');

    commit('updateState', { object: state, property: 'currentAffiliatePayout', value: null });

    dispatch('getAvailableAffiliatePayouts');

    throw error;
  }

  commit('resetPayouts', 'requestedAffiliatePayouts');

  commit('resetPaginatableCollection', state.payoutDeductions);

  dispatch('getRequestedAffiliatePayouts');

  dispatch('getAffiliatePayoutTotals');

  dispatch('getPaymentInfo');

  commit('updateState', { object: state, property: 'currentAffiliatePayout', value: null });

  return response;
};

export const getPayoutDeductionsDetails = async ({ state, dispatch, commit }, { payout, individual }) => {
  let response = await dispatch('get', get(payout._links, 'self.href') || get(payout._links, 'details.href'));

  commit('lockScroll', true);

  if (individual) {
    let value = {
      amount: response.data.amount,
      payouts: [response.data],
    };

    commit('updateState', { object: state, property: 'currentPayoutDeductionsDetails', value });
  } else {
    let value = {
      ...response.data,
      amount: response.data.total,
    };

    commit('updateState', { object: state, property: 'currentPayoutDeductionsDetails', value });
  }

  return response;
};

export const getReferralDetails = async ({ state, dispatch, commit }, referral) => {
  let response = await dispatch('get', referral._links.self.href);

  commit('lockScroll', true);

  commit('updateState', { object: state, property: 'currentReferralDetails', value: response.data });

  return response;
};

export const getPayoutTotals = async ({ state, dispatch, commit }) => {
  let response = await dispatch('async', () => settingsApi.getPayoutTotals());

  commit('updateState', { object: state, property: 'payoutTotals', value: response.data });

  return response;
};

export const getReferralTotals = async ({ state, dispatch, commit }) => {
  let response = await dispatch('async', () => settingsApi.getReferralTotals());

  commit('updateState', { object: state, property: 'referralTotals', value: response.data });

  return response;
};

export const getAvailableReferrals = async ({ commit, dispatch, state }, lazy) => {
  if (!state.availableReferrals.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getAvailableReferrals(state.availableReferrals));

    commit('setReferrals', { type: 'availableReferrals', referrals: response.data });

    return response;
  }
};

export const getRequestedReferrals = async ({ commit, dispatch, state }, lazy) => {
  if (!state.requestedReferrals.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getRequestedReferrals(state.requestedReferrals));

    commit('setReferrals', { type: 'requestedReferrals', referrals: response.data });

    return response;
  }
};

export const getPaidReferrals = async ({ commit, dispatch, state }, lazy) => {
  if (!state.paidReferrals.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getPaidReferrals(state.paidReferrals));

    commit('setReferrals', { type: 'paidReferrals', referrals: response.data });

    return response;
  }
};

export const getCurrentCycleReferrals = async ({ commit, dispatch, state }, lazy) => {
  if (!state.currentcycleReferrals.initialized || lazy) {
    let response = await dispatch('async', () => settingsApi.getCurrentCycleReferrals(state.currentcycleReferrals));

    commit('setReferrals', { type: 'currentcycleReferrals', referrals: response.data });

    return response;
  }
};

export const getAffiliateId = async ({ rootState, state, dispatch, commit }) => {
  let response = await dispatch('get', rootState.currentUser._links.get_affiliate_id.href);

  commit('updateState', { object: state, property: 'affiliateId', value: `aid=${response.data.affiliate_code}` });

  return response;
};

export const getAvailableAffiliatePayouts = async ({ commit, dispatch, state }, lazy) => {
  if (!state.availableAffiliatePayouts.initialized || lazy) {
    let { page, limit, sortColumn, sortDirection } = state.availableAffiliatePayouts,
      url = hateoas.url('search_requestable_affiliate_payouts', { clientId: storage.get('client-hash') });

    url = `${url}?page=${page}&limit=${limit}&sort=${sortColumn}:${sortDirection === 0 ? 'DESC' : 'ASC'}`;

    let response = await dispatch('get', url);

    commit('setPayouts', { type: 'availableAffiliatePayouts', payouts: response.data });

    return response;
  }
};

export const getRequestedAffiliatePayouts = async ({ commit, dispatch, state }, lazy) => {
  if (!state.requestedAffiliatePayouts.initialized || lazy) {
    let { page, limit, sortColumn, sortDirection } = state.requestedAffiliatePayouts,
      url = hateoas.url('search_requested_affiliate_payouts', { clientId: storage.get('client-hash') });

    url = `${url}?page=${page}&limit=${limit}&sort=${sortColumn}:${sortDirection === 0 ? 'DESC' : 'ASC'}`;

    let response = await dispatch('get', url);

    commit('setPayouts', { type: 'requestedAffiliatePayouts', payouts: response.data });

    return response;
  }
};

export const getPendingAffiliatePayouts = async ({ commit, dispatch, state }, lazy) => {
  if (!state.pendingAffiliatePayouts.initialized || lazy) {
    let { page, limit, sortColumn, sortDirection } = state.pendingAffiliatePayouts,
      url = hateoas.url('search_pending_affiliate_payouts', { clientId: storage.get('client-hash') });

    url = `${url}?page=${page}&limit=${limit}&sort=${sortColumn}:${sortDirection === 0 ? 'DESC' : 'ASC'}`;

    let response = await dispatch('get', url);

    commit('setPayouts', { type: 'pendingAffiliatePayouts', payouts: response.data });

    return response;
  }
};

export const getAffiliatePayoutTotals = async ({ state, dispatch, commit }) => {
  let response = await dispatch('get', hateoas.url('get_affiliate_payouts_totals', { clientId: storage.get('client-hash') }));

  commit('updateState', { object: state, property: 'affiliatePayoutTotals', value: response.data });

  return response;
};

export const showPaymentDialog = async ({ rootState, state }, amount) => {
  let response,
    html = '',
    currency = vueMixins.methods.currency,
    totalDeduction = state.payoutTotals.discounts.pending,
    hasRemainingDeduction = amount - totalDeduction < 0,
    requestAmount = hasRemainingDeduction ? 0 : amount - totalDeduction;

  if (totalDeduction > 0) {
    html += `<p><strong>Available Payout:</strong> ${currency(amount)}</p>`;
    if (hasRemainingDeduction) {
      html += `<p><strong>Deductions:</strong> ${currency(amount)} (of ${currency(totalDeduction)})</p>`;
    } else {
      html += `<p><strong>Deductions:</strong> ${currency(totalDeduction)}</p>`;
    }
  }

  let inputOptions = {
    PAYPAL: 'PayPal',
  };

  if (!rootState.currentUser.is_international) {
    inputOptions.ACH = 'ACH';
  }

  inputOptions = {
    ...inputOptions,
    BANK: 'Check (USA Only)',
    PAYONEER: 'Payoneer',
  };

  if (config.PINGPONG_ENABLED && rootState.currentUser.enable_pingpong) {
    inputOptions.PINGPONG = 'PingPong';
  }

  if (rootState.currentUser.allow_wire_payments) {
    inputOptions.WIRE = 'International Wire';
  }

  if (rootState.currentUser.payoneer_only) {
    inputOptions = {
      PAYONEER: 'Payoneer',
    };
  }

  const { value } = await swal({
    title: `Request Payout for ${currency(requestAmount)}?`,
    showCancelButton: true,
    confirmButtonColor: config.PRIMARY_COLOR,
    confirmButtonText: 'Request Payout',
    input: 'select',
    inputPlaceholder: 'Select Payment Method...',
    inputOptions,
    html,
    inputValidator: (value) => {
      return new Promise(function(resolve, reject) {
        if (value) {
          resolve();
        } else {
          reject('Please Select a Payment Method!');
        }
      });
    }
  });

  return value;
};

export const getReferredUsers = async ({ state, dispatch, commit }, { lazy, query, reset } = {}) => {
  if (!state.referredUsers.initialized || lazy || reset) {
    let url = hateoas.url('client_referred_users', { clientId: storage.get('client-hash') });

    if (query) {
      url = `${url}?filters[]=referred_user_email_like:${encodeURIComponent(query)}`;
    }

    let response = await dispatch('get', url);

    if (reset) {
      commit('resetPaginatableCollection', state.referredUsers);
    }

    commit('setPaginatableCollection', { paginatableCollection: state.referredUsers, data: response.data });

    return response
  }
};